import { useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentApi } from '@/entities/payment-claim/api';
import type { PaymentClaim } from '@/entities/payment-claim/types';
import { ApiError } from '@/shared/api/client';
import { GlassCard } from '@/shared/ui/GlassCard';
import { MonoAmount } from '@/shared/ui/MonoAmount';
import { amountText } from '@/shared/lib/money';
import { useT } from '@/shared/lib/i18n';
import { cn } from '@/shared/lib/cn';

interface PaymentConfirmationProps {
  claim: PaymentClaim;
  subscriptionName: string;
  compact?: boolean;
  onSettled?: () => void;
}

export function PaymentConfirmation({
  claim,
  subscriptionName,
  compact = false,
  onSettled,
}: PaymentConfirmationProps) {
  const t = useT();
  const qc = useQueryClient();

  const invalidate = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['payment-claim', claim.id] }),
      qc.invalidateQueries({ queryKey: ['holder-payment-claim', claim.id] }),
      qc.invalidateQueries({ queryKey: ['subscription-history', claim.subscription_id] }),
      qc.invalidateQueries({ queryKey: ['subscription-operations', claim.subscription_id] }),
      qc.invalidateQueries({ queryKey: ['subscriptions', 'holder'] }),
      qc.invalidateQueries({ queryKey: ['subscription', claim.subscription_id] }),
    ]);
    onSettled?.();
  };

  const confirmM = useMutation({
    mutationFn: () => paymentApi.confirm(claim.id),
    onSuccess: invalidate,
  });
  const rejectM = useMutation({
    mutationFn: () => paymentApi.reject(claim.id),
    onSuccess: invalidate,
  });
  const receiptM = useMutation({
    mutationFn: () => paymentApi.requestReceipt(claim.id),
    onSuccess: invalidate,
  });
  const viewReceiptM = useMutation({
    mutationFn: () => paymentApi.getReceipt(claim.id),
    onSuccess: (result) => {
      window.open(result.url, '_blank');
    },
  });

  const busy = confirmM.isPending || rejectM.isPending || receiptM.isPending;
  const hasError = confirmM.isError || rejectM.isError || receiptM.isError;

  return (
    <GlassCard level="content" className={cn('rounded-[18px] p-4', compact && 'p-3')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-status-warn uppercase tracking-wide">
            {t('payment.confirmation.attention')}
          </p>
          <p className="text-[15px] font-semibold truncate mt-0.5">
            {t('payment.confirmation.title', { name: subscriptionName })}
          </p>
          <p className="text-[12px] text-text-muted font-mono mt-1 truncate">
            {claim.participant_id.slice(0, 8)}
            {claim.comment ? ` · ${claim.comment}` : ''}
          </p>
        </div>
        <MonoAmount
          value={amountText(claim.amount, claim.currency)}
          variant="neutral"
          size="md"
          className="text-status-warn"
        />
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3">
        <button
          className="rounded-[13px] py-3 text-[15px] font-semibold text-white bg-brand-gradient shadow-[0_8px_18px_rgba(18,120,90,0.28)] disabled:opacity-50"
          disabled={busy}
          onClick={() => confirmM.mutate()}
        >
          {t('payment.confirmation.confirm')}
        </button>
        <button
          className="rounded-[13px] py-3 text-[15px] font-semibold bg-[rgba(244,251,245,0.55)] border border-white/60 disabled:opacity-50"
          disabled={busy}
          onClick={() => rejectM.mutate()}
        >
          {t('payment.confirmation.reject')}
        </button>
      </div>

      <button
        className="mt-2 w-full rounded-[11px] py-[10px] text-[13px] font-semibold text-brand-green bg-white/45 border border-white/60 disabled:opacity-50"
        disabled={busy}
        onClick={() => receiptM.mutate()}
      >
        {t('payment.confirmation.receipt')}
      </button>

      {claim.has_receipt && (
        <button
          className="mt-2 w-full rounded-[11px] py-[10px] text-[13px] font-semibold text-brand-green bg-white/45 border border-white/60 disabled:opacity-50"
          disabled={viewReceiptM.isPending}
          onClick={() => viewReceiptM.mutate()}
        >
          {t('payment.confirmation.viewReceipt')}
        </button>
      )}

      {viewReceiptM.isError && viewReceiptM.error instanceof ApiError && viewReceiptM.error.status === 409 && (
        <p className="text-[12px] text-text-secondary text-center mt-2">
          {t('payment.confirmation.receiptTelegramOnly')}
        </p>
      )}

      {hasError && (
        <p className="text-[12px] text-status-debt text-center mt-2">
          {t('payment.confirmation.error')}
        </p>
      )}
    </GlassCard>
  );
}
